'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Chip, Skeleton } from '@common/components';
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { AuthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { QuerySuspenseErrorBoundary } from '@/components/organism/QuerySuspenseErrorBoundary';
import { ROUTES } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useLoginHref } from '../../_hooks/useLoginHref';
import { ROULETTE_CATEGORIES, type RouletteCategoryType } from '../_constants';
import { RouletteBrandGrid, RouletteBrandGridFallback, useRouletteBrands } from './RouletteBrandGrid';
const ALL_CATEGORY_OPTION = {
  value: undefined,
  label: '전체'
};
const PartnerPointBalance = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthQueryOptions["get:/wello/v1/auth/affiliate/partner-points/summary"](),
      select: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: balance
  } = useSuspenseQuery(t0);
  let t1;
  if ($[1] !== balance) {
    t1 = balance.toLocaleString();
    $[1] = balance;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <>{t1}</>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const CHEVRON_CLASS_NAME = 'size-16 text-Gray-500 transition group-hover:translate-x-1 group-focus-visible:translate-x-1';
const BALANCE_LINK_CLASS_NAME = cn('group flex items-center justify-between gap-12 rounded-medium border border-B-50 bg-Gray-white px-16 py-12 font-700 text-Gray-800 typo-body4 xl:shrink-0', INTERACTIVE_SURFACE);
const MemberBalance = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="font-400 text-Gray-700 typo-body4">사용 가능한 제휴포인트</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PointCoin currency="AFFILIATE_PARTNER_POINT" size={35} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <>{t0}<QuerySuspenseErrorBoundary errorFallback={_temp2}><p className="flex items-center gap-4 xl:gap-8">{t1}<span className="flex items-baseline gap-2 text-Gray-900 xl:gap-4"><span className="font-800 typo-h4"><Suspense fallback={<Skeleton typo="h4" className="w-96 rounded-xxsmall" />}><PartnerPointBalance /></Suspense></span><span className="font-700 typo-h6">P</span></span></p></QuerySuspenseErrorBoundary></>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const GuestLoginBanner = () => {
  const $ = _c(4);
  const loginHref = useLoginHref();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="font-700 text-Gray-900 typo-body2">로그인하고 쿠폰 룰렛에 도전해보세요!</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col gap-6">{t0}<p className="font-600 text-Gray-600 typo-caption1">쿠팡 쇼핑으로 모은 제휴포인트를 <br className="xl:hidden" />커피·디저트 쿠폰으로 바꿀 수 있어요.</p></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== loginHref) {
    t2 = <section className="px-20 pt-16 xl:p-0"><div className="flex flex-col gap-16 rounded-medium bg-B-25 p-16 xl:flex-row xl:items-center xl:justify-between xl:gap-20 xl:rounded-large xl:p-28">{t1}<Button asChild={true} dimensions="H48" className="px-24 xl:shrink-0"><CustomLink href={loginHref}>로그인하기</CustomLink></Button></div></section>;
    $[2] = loginHref;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const MemberBalanceCard = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-4 xl:gap-6"><MemberBalance /><p className="font-500 text-Gray-500 typo-caption1">쿠팡 쇼핑으로 모은 제휴포인트예요 · 12개월 안에 사용해 주세요</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <section className="px-20 pt-16 xl:p-0"><div className="flex flex-col gap-16 rounded-medium bg-B-25 p-16 xl:flex-row xl:items-center xl:justify-between xl:gap-20 xl:rounded-large xl:p-28">{t0}<CustomLink href={ROUTES.POINT_MARKET_HISTORY.pathname} className={BALANCE_LINK_CLASS_NAME}>내 쿠폰함<SvgChevronright className={CHEVRON_CLASS_NAME} /></CustomLink></div></section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const RouletteCategoryChips = t0 => {
  const $ = _c(11);
  const {
    categoryOptions,
    categoryType
  } = t0;
  const router = useCustomRouter();
  let t1;
  if ($[0] !== router) {
    t1 = nextCategoryType => {
      router.replace(ROUTES.BENEFIT_PARTNER_POINT_SHOP.withSearchParams({
        searchParams: {
          category: nextCategoryType
        }
      }), {
        scroll: false
      });
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSelect = t1;
  let t2;
  if ($[2] !== categoryOptions || $[3] !== categoryType || $[4] !== handleSelect) {
    let t3;
    if ($[6] !== categoryType || $[7] !== handleSelect) {
      t3 = t4 => {
        const {
          value,
          label
        } = t4;
        return <Chip key={label} type="radio" name="roulette-category" className="shrink-0" checked={categoryType === value} onCheckedChange={() => handleSelect(value)}>{label}</Chip>;
      };
      $[6] = categoryType;
      $[7] = handleSelect;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = categoryOptions.map(t3);
    $[2] = categoryOptions;
    $[3] = categoryType;
    $[4] = handleSelect;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <div role="radiogroup" aria-label={"\uCE74\uD14C\uACE0\uB9AC"} className="flex gap-8 overflow-x-auto px-20 hide-scrollbar webkit-scroll-touch xl:px-0">{t2}</div>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
interface RouletteBrandSectionProps {
  isGuest: boolean;
  categoryType: RouletteCategoryType | undefined;
  isWelloApp: boolean;
  onAppOnlyClick: () => void;
}
const RouletteBrandSection = t0 => {
  const $ = _c(16);
  const {
    isGuest,
    categoryType,
    isWelloApp,
    onAppOnlyClick
  } = t0;
  const {
    data: brands
  } = useRouletteBrands(isGuest);
  let activeCategoryType;
  let t1;
  if ($[0] !== brands || $[1] !== categoryType) {
    let t2;
    if ($[4] !== brands) {
      t2 = category => brands.some(brand => brand.categoryType === category.value);
      $[4] = brands;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const categories = ROULETTE_CATEGORIES.filter(t2);
    activeCategoryType = categories.some(category_0 => category_0.value === categoryType) ? categoryType : undefined;
    t1 = <RouletteCategoryChips categoryOptions={[ALL_CATEGORY_OPTION, ...categories]} categoryType={activeCategoryType} />;
    $[0] = brands;
    $[1] = categoryType;
    $[2] = activeCategoryType;
    $[3] = t1;
  } else {
    activeCategoryType = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[6] !== activeCategoryType || $[7] !== brands) {
    t2 = activeCategoryType ? brands.filter(brand_0 => brand_0.categoryType === activeCategoryType) : brands;
    $[6] = activeCategoryType;
    $[7] = brands;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isWelloApp || $[10] !== onAppOnlyClick || $[11] !== t2) {
    t3 = <RouletteBrandGrid brands={t2} isWelloApp={isWelloApp} onAppOnlyClick={onAppOnlyClick} />;
    $[9] = isWelloApp;
    $[10] = onAppOnlyClick;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t1 || $[14] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[13] = t1;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export const PartnerPointShop = t0 => {
  const $ = _c(14);
  const {
    isGuest: serverIsGuest,
    categoryType
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  const {
    isWelloApp
  } = useCheckWelloApp();
  const setIsShowAppInstallConfirmModal = useGlobalStore(_temp3);
  let t1;
  if ($[0] !== isGuest) {
    t1 = isGuest ? <GuestLoginBanner /> : <MemberBalanceCard />;
    $[0] = isGuest;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="sr-only">브랜드 룰렛</h2>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <><RouletteCategoryChips categoryOptions={[ALL_CATEGORY_OPTION]} categoryType={undefined} /><RouletteBrandGridFallback /></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== setIsShowAppInstallConfirmModal) {
    t4 = () => setIsShowAppInstallConfirmModal(true);
    $[4] = setIsShowAppInstallConfirmModal;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== categoryType || $[7] !== isGuest || $[8] !== isWelloApp || $[9] !== t4) {
    t5 = <section className="flex flex-col gap-20 xl:gap-28 xl:rounded-large xl:border xl:border-Gray-200 xl:bg-Gray-white xl:p-32">{t2}<QuerySuspenseErrorBoundary suspenseFallback={t3} errorFallback={_temp4}><RouletteBrandSection isGuest={isGuest} categoryType={categoryType} isWelloApp={isWelloApp} onAppOnlyClick={t4} /></QuerySuspenseErrorBoundary></section>;
    $[6] = categoryType;
    $[7] = isGuest;
    $[8] = isWelloApp;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <div className="mx-auto flex w-full max-w-[750px] flex-col gap-24 pb-40 xl:pb-0">{t1}{t5}</div>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
function _temp(t0) {
  const {
    context
  } = t0;
  return Math.max(0, context?.balance ?? 0);
}
function _temp2(t0) {
  const {
    resetErrorBoundary
  } = t0;
  return <div className="flex items-center gap-12"><p className="font-500 text-Gray-600 typo-body4">불러오지 못했어요</p><Button theme="sub-line" dimensions="H40" onClick={resetErrorBoundary}>다시 시도</Button></div>;
}
function _temp3(state) {
  return state.setIsShowAppInstallConfirmModal;
}
function _temp4(t0) {
  const {
    resetErrorBoundary
  } = t0;
  return <div className="flex min-h-240 flex-col items-center justify-center gap-12 px-20"><p className="font-500 text-Gray-600 typo-body4">불러오지 못했어요</p><Button theme="sub-line" dimensions="H40" onClick={resetErrorBoundary}>다시 시도</Button></div>;
}